import { Component, ComponentType } from "@/types";
import { commandParser } from "./commands/CommandParser";
import { PropertyChange } from "./commands/BaseCommandHandler";
import {
  parseHeadingLevelPresetChange,
  textCommandHandler,
} from "./commands/handlers/TextCommandHandler";
import { buttonCommandHandler } from "./commands/handlers/ButtonCommandHandler";
import { chartCommandHandler } from "./commands/handlers/ChartCommandHandler";
import { mapCommandHandler } from "./commands/handlers/MapCommandHandler";
import { flexContainerCommandHandler } from "./commands/handlers/FlexContainerCommandHandler";
import { quickCommandHandler } from "./commands/handlers/QuickCommandHandler";
import { iconCommandHandler } from "./commands/handlers/IconCommandHandler";
import { dataTextCommandHandler } from "./commands/handlers/DataTextCommandHandler";
import { radialDialCommandHandler } from "./commands/handlers/RadialDialCommandHandler";
import { toggleSwitchCommandHandler } from "./commands/handlers/ToggleSwitchCommandHandler";
import { statusLightCommandHandler } from "./commands/handlers/StatusLightCommandHandler";
import { pushButtonCommandHandler } from "./commands/handlers/PushButtonCommandHandler";
import { iconButtonCommandHandler } from "./commands/handlers/IconButtonCommandHandler";
import { stepperCommandHandler } from "./commands/handlers/StepperCommandHandler";
import { sliderCommandHandler } from "./commands/handlers/SliderCommandHandler";
import { levelIndicatorCommandHandler } from "./commands/handlers/LevelIndicatorCommandHandler";
import { segmentedControlCommandHandler } from "./commands/handlers/SegmentedControlCommandHandler";
import { colorPickerCommandHandler } from "./commands/handlers/ColorPickerCommandHandler";
import { thermostatCommandHandler } from "./commands/handlers/ThermostatCommandHandler";
import { styleCloneService } from "./StyleCloneService";

const MULTI_SELECT_PARSE_BATCH_SIZE = 8;

async function mapInBatches<T, R>(
  items: T[],
  batchSize: number,
  mapper: (item: T, index: number) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];

  for (let startIndex = 0; startIndex < items.length; startIndex += batchSize) {
    const batch = items.slice(startIndex, startIndex + batchSize);
    const batchResults = await Promise.all(
      batch.map((item, batchIndex) => mapper(item, startIndex + batchIndex))
    );
    results.push(...batchResults);
  }

  return results;
}

function getNumberValue(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

function getStringValue(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

class PropertyModificationService {
  private commandCache = new Map<string, PropertyChange>();
  private patternSuccessRate = new Map<string, number>();

  /**
   * CRITICAL: These patterns indicate commands that depend on current component state.
   * These commands MUST NOT be cached because they calculate values based on current state.
   *
   * Categories:
   * 1. Relative size commands (taller, wider, etc.) - depend on current dimensions
   * 2. State-dependent commands (transparency, opacity) - depend on current background (gradient vs solid)
   *
   * Caching them causes incorrect results, e.g.:
   * - "taller" → "taller" would return the same value
   * - "50% transparent" after adding a gradient would use cached rgba instead of CSS opacity
   */
  private static readonly RELATIVE_COMMAND_PATTERNS = [
    /\b(taller|shorter|wider|narrower|bigger|larger|smaller|tinier)\b/i,
    /\b(double|triple|half|halve)\b/i,
    /\b(\d+%\s*(larger|bigger|smaller|tinier|wider|narrower|taller|shorter))\b/i,
    /\b(increase|decrease|grow|shrink|expand|reduce)\b/i,
    /\b(much|way|a\s*lot|bit|little|slightly)\s*(bigger|larger|smaller|tinier)\b/i,
    /\b(more|less)\s*(padding|margin|gap|transparent|opaque|rounded|shadow)\b/i,
    // State-dependent commands: transparency/opacity depends on current background (gradient vs solid)
    /\b(transparent|transparency|opacity)\b/i,
    /\bsemi[\s-]?transparent\b/i,
    // Phase 6: Color brightness commands depend on current background color
    /\b(darker|lighter)\b/i,
    // Phase 2: Relative font weight commands depend on current fontWeight
    /\bbolder\b/i,
    /\bweight\s+lighter\b/i,
    /\bfont\s+lighter\b/i,
    /\blighter\s+(?:font\s+)?weight\b/i,
    /\bless\s+bold\b/i,
    /\bnot\s+(?:as\s+)?bold\b/i,
    // Map zoom commands depend on current zoom level
    /\bzoom\s*(in|out)\b/i,
  ];

  /**
   * Check if a command is relative (depends on current dimensions)
   * Relative commands should NOT be cached.
   */
  private isRelativeCommand(input: string): boolean {
    const normalized = input.trim().toLowerCase();
    return PropertyModificationService.RELATIVE_COMMAND_PATTERNS.some(pattern => pattern.test(normalized));
  }

  /**
   * Parse natural language commands into property changes (synchronous version for compatibility)
   * Uses a hybrid approach for better text support
   */
  parsePropertyChanges(input: string, component: Component): PropertyChange | null {
    const trimmedInput = input.trim();
    const headingLevelChange = component.type === ComponentType.TEXT
      ? parseHeadingLevelPresetChange(trimmedInput)
      : null;

    // Check cache first for repeated commands
    // IMPORTANT: Do NOT use cache for relative commands (taller, wider, bigger, etc.)
    // These must be recalculated each time based on current dimensions
    const cacheKey = `${component.id}:${input}`;
    const isRelative = this.isRelativeCommand(input);

    // Map commands depend on selectedPinId context, not just component ID + command text.
    // Commands like "use camera icon" or "make pin green" target whichever pin is selected,
    // so the same command text produces different results — skip cache entirely for maps.
    const isMapCommand = component.type === ComponentType.MAP_CHART;

    if (headingLevelChange) {
      if (!isRelative && !isMapCommand) {
        this.commandCache.set(cacheKey, headingLevelChange);
      }
      this.trackPatternSuccess(input, true);
      return headingLevelChange;
    }

    if (!isRelative && !isMapCommand && this.commandCache.has(cacheKey)) {
      const cachedCommand = this.commandCache.get(cacheKey);
      if (cachedCommand !== undefined) {
        return cachedCommand;
      }
    }

    // Synchronous callers use the local compatibility patterns below. Runtime chat
    // quick commands should call parsePropertyChangesAsync so CommandParser remains
    // the canonical first pass.
    try {
      const quickResult = this.tryQuickPatterns(input, component);
      if (quickResult) {
        // Only cache non-relative commands (relative commands depend on current dimensions)
        if (!isRelative && !isMapCommand) {
          this.commandCache.set(cacheKey, quickResult);
        }
        this.trackPatternSuccess(input, true);
        return quickResult;
      }
    } catch (error) {
      console.warn('Command parser error, falling back to legacy patterns:', error);
    }

    // For flex-container components, use pattern matching synchronously
    if ((component.type === ComponentType.FLEX_CONTAINER ||
         component.type === ComponentType.CONTAINER)) {
      // Check for common flex-container patterns
      // IMPORTANT: Use 'size' not 'style' for dimensions - components fill their wrapper
      const flexPatterns: Array<[RegExp, PropertyChange]> = [
        [/^(?:make |set )?(?:the )?(?:height |it )?full[\s-]?page$/i,
         { size: { height: '100vh' }, properties: { minHeight: '100vh' } }],
        [/^(?:make |set )?(?:the )?height\s+full[\s-]?page$/i,
         { size: { height: '100vh' }, properties: { minHeight: '100vh' } }],
        [/^(?:make |set )?(?:it |the container )?100vh$/i,
         { size: { height: '100vh' }, properties: { minHeight: '100vh' } }],
        [/^(?:make |set )?full[\s-]?height$/i,
         { size: { height: '100%' }, properties: { minHeight: '100%' } }],
        [/^center\s+(?:all |everything|items|children)$/i,
         { properties: { justifyContent: 'center', alignItems: 'center' } }],
        [/^stack\s+(?:items |children )?(?:vertically|vertical)$/i,
         { properties: { flexDirection: 'column' } }],
        [/^stack\s+(?:items |children )?(?:horizontally|horizontal)$/i,
         { properties: { flexDirection: 'row' } }],
      ];

      for (const [pattern, change] of flexPatterns) {
        if (pattern.test(input.trim())) {
          // Only cache non-relative commands
          if (!isRelative) {
            this.commandCache.set(cacheKey, change);
          }
          this.trackPatternSuccess(input, true);
          return change;
        }
      }
    }

    // For text components, use enhanced parsing
    if (component.type === ComponentType.TEXT && textCommandHandler.canHandle(input, component)) {
      // Use the pattern matching from the handler synchronously
      const changes: PropertyChange = { properties: {}, style: {} };
      // IMPORTANT: Trim input for pattern matching (anchored patterns like ^...$ require exact match)
      const trimmedInput = input.trim();

      // Try to match against text handler patterns
      const patterns: Array<[RegExp, (match: RegExpMatchArray) => PropertyChange | null]> = [
        // Content patterns
        [/(?:set |change )?(?:text |content )(?:to )?["']([^"']+)["']/i,
         (match) => ({ properties: { content: match[1] } })],

        // Font size patterns
        [/(?:set |change )?(?:font |text )?size (?:to )?(\d+)(?:px)?/i,
         (match) => ({ properties: { fontSize: parseInt(match[1], 10) } })],

        // Relative size patterns
        [/(?:make |set )?(?:text )?(bigger|larger|increase)/i,
         () => {
           const currentSize = getNumberValue(component.properties?.fontSize) ?? 16;
           return { properties: { fontSize: Math.min(currentSize + 4, 72) } };
         }],
        [/(?:make |set )?(?:text )?(smaller|decrease)/i,
         () => {
           const currentSize = getNumberValue(component.properties?.fontSize) ?? 16;
           return { properties: { fontSize: Math.max(currentSize - 4, 8) } };
         }],

        // Heading patterns
        [/(?:make |set |change )?(?:it |to |as )?(?:heading |h)([1-6])/i,
         (match) => {
           const headingSizes: Record<string, number> = {
             '1': 32, '2': 28, '3': 24, '4': 20, '5': 18, '6': 16
           };
           return {
             properties: {
               fontSize: headingSizes[match[1]] || 16,
               fontWeight: 'bold'
             }
           };
         }],

        // Named size patterns
        [/(?:make |set )?(?:text )?(tiny|small|medium|large|huge|extra[\s-]?large)/i,
         (match) => {
           const sizes: Record<string, number> = {
             'tiny': 10, 'small': 12, 'medium': 16,
             'large': 20, 'huge': 28, 'extra large': 36, 'extra-large': 36
           };
           return { properties: { fontSize: sizes[match[1].toLowerCase().replace('-', ' ')] || 16 } };
         }],

        // Font weight patterns
        [/(?:make |set )?(?:text )?(bold|normal|light|thin)/i,
         (match) => ({ properties: { fontWeight: match[1] === 'bold' ? 'bold' : match[1] === 'light' || match[1] === 'thin' ? '300' : 'normal' } })],

        // Font style patterns
        [/(?:make |set )?(?:text )?(italic|normal)/i,
         (match) => ({ properties: { fontStyle: match[1] } })],

        // Alignment patterns
        [/(?:align |set alignment )?(left|center|right|justify)/i,
         (match) => ({ properties: { textAlign: match[1] } })],

        // Color patterns
        [/(?:set |change )?(?:text )?color (?:to )?([#\w]+)/i,
         (match) => ({ style: { color: this.normalizeColor(match[1]) } })],

        // Phase 1: Extended CSS color patterns (color-first syntax: "navy text", "coral text")
        [/^(navy|indigo|teal|coral|salmon|crimson|maroon|olive|lime|aqua|turquoise|violet|plum|gold|silver|beige|ivory|lavender|mint|peach|tan|sienna|azure|chocolate|tomato|khaki|orchid|peru)\s+(?:text|color)$/i,
         (match) => {
           console.log(`[Phase 1.1] Extended CSS color matched in PropertyModificationService: ${match[1]}`);
           return { style: { color: match[1].toLowerCase() } };
         }],

        // Phase 1: Direct extended CSS color names (single word)
        [/^(navy|indigo|teal|coral|salmon|crimson|maroon|olive|lime|aqua|turquoise|violet|plum|gold|silver|beige|ivory|lavender|mint|peach|tan|sienna|azure|chocolate|tomato|khaki|orchid|peru)$/i,
         (match) => {
           console.log(`[Phase 1.1] Direct extended CSS color matched: ${match[1]}`);
           return { style: { color: match[1].toLowerCase() } };
         }],

        // Phase 1: Brand colors
        [/^(forest\s*green|forest|primary|brand|sage\s*green|sage|secondary|seafoam|accent|blue\s*grotto|grotto|info|link|desert\s*sun|desert|warning|chartreuse|charcoal|muted|champagne|cream)(?:\s+(?:text|color))?$/i,
         (match) => {
           const brandColors: Record<string, string> = {
             'forest green': '#4B633B', 'forest': '#4B633B', 'primary': '#4B633B', 'brand': '#4B633B',
             'sage green': '#B2AC88', 'sage': '#B2AC88', 'secondary': '#B2AC88',
             'seafoam': '#93E9BE', 'accent': '#93E9BE',
             'blue grotto': '#116B98', 'grotto': '#116B98', 'info': '#116B98', 'link': '#116B98',
             'desert sun': '#E27D3D', 'desert': '#E27D3D', 'warning': '#E27D3D',
             'chartreuse': '#CCFF00', 'charcoal': '#0A0A0A', 'muted': '#6E6E6E',
             'champagne': '#F7E7CE', 'cream': '#F7E7CE',
           };
           const colorKey = match[1].toLowerCase().replace(/\s+/g, ' ');
           const hexColor = brandColors[colorKey];
           if (hexColor) {
             console.log(`[Phase 1.2] Brand color matched: "${colorKey}" -> ${hexColor}`);
             return { style: { color: hexColor } };
           }
           return null;
         }],

        // Phase 1: Semantic colors
        [/^(success|error|danger|valid|invalid|positive|negative|neutral|disabled)(?:\s+(?:text|color))?$/i,
         (match) => {
           const semanticColors: Record<string, string> = {
             'success': '#22C55E', 'valid': '#22C55E', 'positive': '#22C55E',
             'error': '#EF4444', 'danger': '#EF4444', 'invalid': '#EF4444', 'negative': '#EF4444',
             'neutral': '#6B7280', 'disabled': '#9CA3AF',
           };
           const colorKey = match[1].toLowerCase();
           const hexColor = semanticColors[colorKey];
           if (hexColor) {
             console.log(`[Phase 1.4] Semantic color matched: "${colorKey}" -> ${hexColor}`);
             return { style: { color: hexColor } };
           }
           return null;
         }],
      ];

      for (const [pattern, handler] of patterns) {
        const match = trimmedInput.match(pattern);
        if (match) {
          const result = handler(match);
          if (result) {
            if (result.properties) {
              changes.properties = { ...changes.properties, ...result.properties };
            }
            if (result.style) {
              changes.style = { ...changes.style, ...result.style };
            }
          }
        }
      }

      // Return if we found any changes
      if (Object.keys(changes.properties || {}).length > 0 ||
          Object.keys(changes.style || {}).length > 0) {
        return changes;
      }
    }

    // For button components, reuse the dedicated button matcher so page navigation
    // commands resolve to page IDs before falling back to the generic AI path.
    if (component.type === ComponentType.BUTTON && buttonCommandHandler.canHandle(trimmedInput, component)) {
      const buttonResult = buttonCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (buttonResult) {
        if (buttonResult.properties?.action === 'form-submit') {
          return null;
        }

        const buttonTarget = getStringValue(buttonResult.properties?.target);
        if (buttonResult.properties?.action === 'navigate' && buttonTarget) {
          const pageTarget = buttonTarget;
          const resolution = buttonCommandHandler.tryResolvePageTarget(pageTarget);

          if (resolution.pageId) {
            buttonResult.properties.target = resolution.pageId;
            buttonResult.properties.navigationLabel = resolution.pageName;
          } else if (resolution.error) {
            return null;
          }
        }

        return buttonResult;
      }
    }

    // For chart components, use basic chart patterns
    if (chartCommandHandler.canHandle(input, component)) {
      const changes: PropertyChange = { properties: {} };

      // Title pattern
      const titleMatch = input.match(/(?:set |change )?title (?:to )?["']([^"']+)["']/i);
      if (titleMatch) {
        changes.properties = { ...changes.properties, title: titleMatch[1] };
      }

      // Color themes
      const colorMatch = input.match(/(?:use |apply |set )?(?:color |theme |palette )?(?:to )?(blue|red|green|purple|orange|yellow|rainbow|pastel|agricultural)/i);
      if (colorMatch) {
        const colorMap: Record<string, string[]> = {
          blue: ["#3B82F6", "#60A5FA", "#93C5FD"],
          red: ["#EF4444", "#F87171", "#FCA5A5"],
          green: ["#10B981", "#34D399", "#6EE7B7"],
          purple: ["#8B5CF6", "#A78BFA", "#C4B5FD"],
          orange: ["#F97316", "#FB923C", "#FDBA74"],
          yellow: ["#F59E0B", "#FCD34D", "#FDE68A"],
          rainbow: ["#FF0000", "#FF7F00", "#FFFF00", "#00FF00", "#0000FF", "#4B0082", "#9400D3"],
          pastel: ["#FFB3BA", "#FFDFBA", "#FFFFBA", "#BAFFC9", "#BAE1FF"],
          agricultural: ["#8BC34A", "#689F38", "#558B2F", "#33691E"]
        };
        const theme = colorMatch[1].toLowerCase();
        if (colorMap[theme]) {
          changes.properties = { ...changes.properties, colors: colorMap[theme] };
        }
      }

      if (Object.keys(changes.properties || {}).length > 0) {
        return changes;
      }
    }

    // For other components or unmatched commands, use legacy parsing
    return this.parseLegacyPropertyChanges(input, component);
  }

  /**
   * Parse natural language commands into property changes (async version)
   * Uses the new scalable command system
   */
  async parsePropertyChangesAsync(input: string, component: Component): Promise<PropertyChange | null> {

    // Use the new command parser system
    const result = await commandParser.parseCommand(input, component);

    if (result.success && result.command?.changes) {
      return result.command.changes;
    }

    // Fallback to legacy parsing for backward compatibility
    return this.parseLegacyPropertyChanges(input, component);
  }

  /**
   * Try quick patterns for immediate response
   */
  private tryQuickPatterns(input: string, component: Component): PropertyChange | null {
    const trimmedInput = input.trim();

    // Phase 2.3: Check for style clone commands first
    // Clone commands require async processing (component resolution), so return null to fall through
    const cloneInfo = styleCloneService.parseCloneCommand(trimmedInput);
    if (cloneInfo) {
      // Return null to let the command fall through to async processing
      return null;
    }

    // Phase 7: Universal help commands (work regardless of component type)
    // "list presets", "show presets", "what are the presets?", etc.
    if (/^(?:list|show|what(?:\s+are)?(?:\s+the)?|available|get)\s+(?:style\s+)?presets\??$/i.test(trimmedInput)) {
      return { properties: { _presetInfo: true } };
    }

    // Phase 1 (Flex Container Enhancement): Try FlexContainerCommandHandler FIRST for flex-container components
    // This ensures brand colors, gradients, and opacity patterns are processed correctly
    // before QuickCommandHandler's generic catch-all patterns intercept them
    const isFlexContainer = component.type === ComponentType.FLEX_CONTAINER ||
                            component.type === ComponentType.CONTAINER;
    if (isFlexContainer && flexContainerCommandHandler.canHandle(trimmedInput, component)) {
      const flexResult = flexContainerCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (flexResult) {
        return flexResult;
      }
    }

    // Phase 2.2: Try the QuickCommandHandler for all relative/comparative patterns
    // This includes: 50% larger, double the size, a bit bigger, conversational phrases, etc.
    if (quickCommandHandler.canHandle(trimmedInput, component)) {
      // Use matchPatternsNormalized which handles conversational phrases
      const quickResult = quickCommandHandler.matchPatternsNormalized(trimmedInput, component);
      if (quickResult) {
        return quickResult;
      }
    }

    // Phase 1: Direct handling for extended CSS color patterns (handles "lavender text", "navy text", etc.)
    // This ensures color patterns are matched BEFORE falling through to TextCommandHandler
    if (component.type === ComponentType.TEXT) {
      // Extended CSS colors: "lavender text", "navy text", "coral color", etc.
      const extendedColorTextMatch = trimmedInput.match(/^(navy|indigo|teal|coral|salmon|crimson|maroon|olive|lime|aqua|turquoise|violet|plum|gold|silver|beige|ivory|lavender|mint|peach|tan|sienna|azure|chocolate|tomato|khaki|orchid|peru|thistle|wheat|linen|snow|honeydew|seashell|firebrick|darkred|darkorange|darkgreen|darkblue|lightblue|lightgreen|lightgray|lightgrey|lightpink|skyblue|steelblue|slategray|slategrey|royalblue|midnightblue|forestgreen|limegreen|springgreen|seagreen)\s+(?:text|color)$/i);
      if (extendedColorTextMatch) {
        console.log(`[Phase 1.1 Direct] Extended CSS color matched: ${extendedColorTextMatch[1]}`);
        return { style: { color: extendedColorTextMatch[1].toLowerCase() } };
      }

      // Single-word extended CSS colors
      const singleColorMatch = trimmedInput.match(/^(navy|indigo|teal|coral|salmon|crimson|maroon|olive|lime|aqua|turquoise|violet|plum|gold|silver|beige|ivory|lavender|mint|peach|tan|sienna|azure|chocolate|tomato|khaki|orchid|peru)$/i);
      if (singleColorMatch) {
        console.log(`[Phase 1.1 Direct] Single-word CSS color matched: ${singleColorMatch[1]}`);
        return { style: { color: singleColorMatch[1].toLowerCase() } };
      }
    }

    // For text components, also try the TextCommandHandler patterns (Phase 2.2 font patterns)
    if (component.type === ComponentType.TEXT && textCommandHandler.canHandle(trimmedInput, component)) {
      const headingLevelChange = parseHeadingLevelPresetChange(trimmedInput);
      if (headingLevelChange) {
        return headingLevelChange;
      }

      const textResult = textCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (textResult) {
        return textResult;
      }
    }

    // For data-text components, try DataTextCommandHandler synchronously
    if (component.type === ComponentType.DATA_TEXT && dataTextCommandHandler.canHandle(trimmedInput, component)) {
      const dataTextResult = dataTextCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (dataTextResult) {
        return dataTextResult;
      }
    }

    // Phase 4.2: For map components, try MapCommandHandler synchronously
    if (component.type === ComponentType.MAP_CHART && mapCommandHandler.canHandle(trimmedInput, component)) {
      // Use matchPatterns synchronously for quick map commands
      const mapResult = mapCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (mapResult) {
        return mapResult;
      }
    }

    // Phase 4.1: For chart components, try ChartCommandHandler synchronously
    const chartTypes = [
      ComponentType.LINE_CHART,
      ComponentType.BAR_CHART,
      ComponentType.PIE_CHART,
      ComponentType.AREA_CHART,
      ComponentType.SCATTER_CHART,
      ComponentType.HEATMAP_CHART,
      ComponentType.TREEMAP_CHART,
      ComponentType.GAUGE_CHART,
      ComponentType.VERTICAL_GAUGE_CHART,
    ];
    if (chartTypes.includes(component.type) && chartCommandHandler.canHandle(trimmedInput, component)) {
      // Use matchPatterns synchronously for quick chart commands
      const chartResult = chartCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (chartResult) {
        return chartResult;
      }
    }

    // For icon components, try IconCommandHandler synchronously
    if (component.type === ComponentType.ICON && iconCommandHandler.canHandle(trimmedInput, component)) {
      const iconResult = iconCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (iconResult) {

        // Resolve page name to ID for navigation actions
        const iconTarget = getStringValue(iconResult.properties?.target);
        if (iconResult.properties?.action === 'navigate' && iconTarget) {
          const pageTarget = iconTarget;
          const resolution = iconCommandHandler.tryResolvePageTarget(pageTarget);

          if (resolution.pageId) {
            iconResult.properties.target = resolution.pageId;
            iconResult.properties.navigationLabel = resolution.pageName;
          } else if (resolution.error) {
            // Return null to fall back to full AI processing which will show the error
            return null;
          }
        }

        return iconResult;
      }
    }

    // For radial dial components, try RadialDialCommandHandler synchronously
    if (component.type === ComponentType.RADIAL_DIAL && radialDialCommandHandler.canHandle(trimmedInput, component)) {
      const dialResult = radialDialCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (dialResult) return dialResult;
    }

    // For toggle switch components, try ToggleSwitchCommandHandler synchronously
    if (component.type === ComponentType.TOGGLE_SWITCH && toggleSwitchCommandHandler.canHandle(trimmedInput, component)) {
      const toggleResult = toggleSwitchCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (toggleResult) return toggleResult;
    }

    // For status light components, try StatusLightCommandHandler synchronously
    if (component.type === ComponentType.STATUS_LIGHT && statusLightCommandHandler.canHandle(trimmedInput, component)) {
      const lightResult = statusLightCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (lightResult) return lightResult;
    }

    // For push button components, try PushButtonCommandHandler synchronously
    if (component.type === ComponentType.PUSH_BUTTON && pushButtonCommandHandler.canHandle(trimmedInput, component)) {
      const pushResult = pushButtonCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (pushResult) return pushResult;
    }

    // For icon button components, try IconButtonCommandHandler synchronously
    if (component.type === ComponentType.ICON_BUTTON && iconButtonCommandHandler.canHandle(trimmedInput, component)) {
      const iconBtnResult = iconButtonCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (iconBtnResult) return iconBtnResult;
    }

    // For stepper components, try StepperCommandHandler synchronously
    if (component.type === ComponentType.STEPPER && stepperCommandHandler.canHandle(trimmedInput, component)) {
      const stepperResult = stepperCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (stepperResult) return stepperResult;
    }

    // For slider components, try SliderCommandHandler synchronously
    if (component.type === ComponentType.SLIDER && sliderCommandHandler.canHandle(trimmedInput, component)) {
      const sliderResult = sliderCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (sliderResult) return sliderResult;
    }

    // For level indicator components, try LevelIndicatorCommandHandler synchronously
    if (component.type === ComponentType.LEVEL_INDICATOR && levelIndicatorCommandHandler.canHandle(trimmedInput, component)) {
      const levelResult = levelIndicatorCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (levelResult) return levelResult;
    }

    // For segmented control components, try SegmentedControlCommandHandler synchronously
    if (component.type === ComponentType.SEGMENTED_CONTROL && segmentedControlCommandHandler.canHandle(trimmedInput, component)) {
      const segResult = segmentedControlCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (segResult) return segResult;
    }

    // For color picker components, try ColorPickerCommandHandler synchronously
    if (component.type === ComponentType.COLOR_PICKER && colorPickerCommandHandler.canHandle(trimmedInput, component)) {
      const cpResult = colorPickerCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (cpResult) return cpResult;
    }

    // For thermostat components, try ThermostatCommandHandler synchronously
    if (component.type === ComponentType.THERMOSTAT && thermostatCommandHandler.canHandle(trimmedInput, component)) {
      const thResult = thermostatCommandHandler.tryMatchPatterns(trimmedInput, component);
      if (thResult) return thResult;
    }

    // File Storage handler - requires async processing, return null to fall through
    if (component.type === ComponentType.FILE_STORAGE) {
      return null;
    }

    // Legacy quick patterns for basic commands
    const quickPatterns: Array<[RegExp, PropertyChange]> = [
      // Visibility
      [/^hide$/i, { style: { display: 'none' } }],
      [/^show$/i, { style: { display: 'block' } }],
      // Colors
      [/^(red|blue|green|yellow|orange|purple|pink|black|white|gray|grey)$/i,
        { style: { color: trimmedInput.toLowerCase() } }],
      // Text styles (for text components)
      [/^bold$/i, { properties: { fontWeight: 'bold' } }],
      [/^italic$/i, { properties: { fontStyle: 'italic' } }],
      [/^underline$/i, { properties: { textDecoration: 'underline' } }],
    ];

    for (const [pattern, change] of quickPatterns) {
      if (pattern.test(trimmedInput)) {
        return change;
      }
    }

    // For text components, check for direct content patterns
    if (component.type === ComponentType.TEXT) {
      // Check if it's just a quoted string
      if (/^["']([^"']+)["']$/.test(trimmedInput)) {
        const match = trimmedInput.match(/^["']([^"']+)["']$/);
        if (match) {
          return { properties: { content: match[1] } };
        }
      }

      // Direct content change pattern
      if (trimmedInput.startsWith('change the content to ') ||
          trimmedInput.startsWith('change content to ')) {
        const content = trimmedInput.replace(/^change (?:the )?content to /i, '');
        return { properties: { content } };
      }

      // Phase 5: Removed legacy fallback that treated unrecognized text as content.
      // Content changes now ONLY happen via explicit quoted patterns (above):
      // - "text" or 'text' - quoted strings
      // - "change the content to X" - explicit content command
      // This prevents confusion between natural language commands like "change the text to red"
      // and intentional content replacement.
    }

    return null;
  }

  /**
   * Track pattern success for learning
   */
  private trackPatternSuccess(pattern: string, success: boolean): void {
    const current = this.patternSuccessRate.get(pattern) || 0;
    this.patternSuccessRate.set(pattern, success ? current + 1 : current);

    // Log highly successful patterns for analysis
    if (current > 10 && current % 10 === 0) {
    }
  }

  /**
   * Legacy property parsing for backward compatibility
   */
  private parseLegacyPropertyChanges(input: string, component: Component): PropertyChange | null {
    const changes: PropertyChange = {};
    const lowerInput = input.toLowerCase();

    // Size modifications
    if (lowerInput.includes('width') || lowerInput.includes('wide')) {
      const widthMatch = input.match(/(\d+)\s*(?:px)?/i);
      if (widthMatch) {
        changes.size = { ...changes.size, width: parseInt(widthMatch[1], 10) };
      }
    }

    if (lowerInput.includes('height') || lowerInput.includes('tall')) {
      const heightMatch = input.match(/height[:\s]+(\d+)\s*(?:px)?/i);
      if (heightMatch) {
        changes.size = { ...changes.size, height: parseInt(heightMatch[1], 10) };
      }
    }

    // Text-specific modifications
    if (component.type === ComponentType.TEXT || lowerInput.includes('text') || lowerInput.includes('font') || lowerInput.includes('heading')) {
      const headingLevelChange = parseHeadingLevelPresetChange(input);
      if (headingLevelChange) {
        return headingLevelChange;
      }

      changes.properties = {};

      // Heading patterns
      const headingMatch = input.match(/(?:heading |h)([1-6])/i);
      // Check if this is a shadow command (to avoid "medium shadow" being treated as "medium" font size)
      const isShadowCommand = lowerInput.includes('shadow');

      if (headingMatch) {
        const headingSizes: Record<string, number> = {
          '1': 32, '2': 28, '3': 24, '4': 20, '5': 18, '6': 16
        };
        changes.properties.fontSize = headingSizes[headingMatch[1]] || 16;
        changes.properties.fontWeight = 'bold';
      }
      // Named sizes (exclude shadow-related commands like "small shadow", "medium shadow")
      else if (lowerInput.includes('tiny') && !isShadowCommand) {
        changes.properties.fontSize = 10;
      } else if (lowerInput.includes('small') && !lowerInput.includes('smaller') && !isShadowCommand) {
        changes.properties.fontSize = 12;
      } else if (lowerInput.includes('medium') && !isShadowCommand) {
        changes.properties.fontSize = 16;
      } else if (lowerInput.includes('large') && !lowerInput.includes('larger') && !isShadowCommand) {
        changes.properties.fontSize = 20;
      } else if (lowerInput.includes('huge') && !isShadowCommand) {
        changes.properties.fontSize = 28;
      } else if ((lowerInput.includes('extra large') || lowerInput.includes('extra-large')) && !isShadowCommand) {
        changes.properties.fontSize = 36;
      }
      // Relative sizes
      else if (lowerInput.includes('bigger') || lowerInput.includes('larger')) {
        const currentSize = getNumberValue(component.properties?.fontSize) ?? 16;
        changes.properties.fontSize = Math.min(currentSize + 4, 72);
      } else if (lowerInput.includes('smaller')) {
        const currentSize = getNumberValue(component.properties?.fontSize) ?? 16;
        changes.properties.fontSize = Math.max(currentSize - 4, 8);
      }
      // Specific size
      else if (lowerInput.includes('size')) {
        const sizeMatch = input.match(/(?:size|font)[:\s]+(\d+)\s*(?:px)?/i);
        if (sizeMatch) {
          changes.properties.fontSize = parseInt(sizeMatch[1], 10);
        }
      }

      // Text alignment
      if (lowerInput.includes('center')) {
        changes.properties.textAlign = 'center';
      } else if (lowerInput.includes('left')) {
        changes.properties.textAlign = 'left';
      } else if (lowerInput.includes('right')) {
        changes.properties.textAlign = 'right';
      }

      // Font weight
      // NOTE: Use word boundary check to avoid matching 'bolder' which should use Phase 2 relative weight logic
      if (/\bbold\b/.test(lowerInput) && !lowerInput.includes('bolder')) {
        changes.properties.fontWeight = 'bold';
      } else if (lowerInput.includes('normal') || lowerInput.includes('regular')) {
        changes.properties.fontWeight = 'normal';
      }

      // Content
      const contentMatch = input.match(/(?:text|content)[:\s]+"([^"]+)"/i) ||
                          input.match(/(?:text|content)[:\s]+'([^']+)'/i);
      if (contentMatch) {
        changes.properties.content = contentMatch[1];
      }
    }

    // Button-specific modifications
    if (component.type === ComponentType.BUTTON) {
      if (!changes.properties) changes.properties = {};

      if (lowerInput.includes('primary')) {
        changes.properties.variant = 'primary';
      } else if (lowerInput.includes('secondary')) {
        changes.properties.variant = 'secondary';
      } else if (lowerInput.includes('outline')) {
        changes.properties.variant = 'outline';
      }

      // Button text
      const textMatch = input.match(/(?:text|label)[:\s]+"([^"]+)"/i) ||
                       input.match(/(?:text|label)[:\s]+'([^']+)'/i);
      if (textMatch) {
        changes.properties.text = textMatch[1];
      }
    }

    // Input-specific modifications
    if (component.type === ComponentType.INPUT) {
      if (!changes.properties) changes.properties = {};

      // Placeholder
      const placeholderMatch = input.match(/placeholder[:\s]+"([^"]+)"/i) ||
                              input.match(/placeholder[:\s]+'([^']+)'/i);
      if (placeholderMatch) {
        changes.properties.placeholder = placeholderMatch[1];
      }

      // Input type
      if (lowerInput.includes('email')) {
        changes.properties.type = 'email';
      } else if (lowerInput.includes('password')) {
        changes.properties.type = 'password';
      } else if (lowerInput.includes('number')) {
        changes.properties.type = 'number';
      } else if (lowerInput.includes('text')) {
        changes.properties.type = 'text';
      }
    }

    // Style modifications
    changes.style = {};
    const styleChanges = changes.style;

    // Colors
    const colorPatterns = [
      { pattern: /(?:background|bg)[:\s]+([#\w]+)/i, property: 'backgroundColor' },
      { pattern: /(?:color|text\s+color)[:\s]+([#\w]+)/i, property: 'color' },
      { pattern: /(?:border\s+color)[:\s]+([#\w]+)/i, property: 'borderColor' },
    ];

    colorPatterns.forEach(({ pattern, property }) => {
      const match = input.match(pattern);
      if (match) {
        styleChanges[property] = this.normalizeColor(match[1]);
      }
    });

    // Common color shortcuts
    if (lowerInput.includes('blue') && !lowerInput.includes('light') && !lowerInput.includes('dark')) {
      if (lowerInput.includes('background') || lowerInput.includes('bg')) {
        changes.style.backgroundColor = '#3b82f6';
      } else if (lowerInput.includes('text') || lowerInput.includes('color')) {
        changes.style.color = '#3b82f6';
      }
    }

    if (lowerInput.includes('red')) {
      if (lowerInput.includes('background') || lowerInput.includes('bg')) {
        changes.style.backgroundColor = '#ef4444';
      } else if (lowerInput.includes('text') || lowerInput.includes('color')) {
        changes.style.color = '#ef4444';
      }
    }

    if (lowerInput.includes('green')) {
      if (lowerInput.includes('background') || lowerInput.includes('bg')) {
        changes.style.backgroundColor = '#10b981';
      } else if (lowerInput.includes('text') || lowerInput.includes('color')) {
        changes.style.color = '#10b981';
      }
    }

    // Border radius
    // Skip for flex-containers - they use FlexContainerCommandHandler for specialized radius handling
    const isFlexContainerForRadius = component.type === ComponentType.FLEX_CONTAINER ||
      component.type === ComponentType.CONTAINER;
    if ((lowerInput.includes('rounded') || lowerInput.includes('radius')) && !isFlexContainerForRadius) {
      const radiusMatch = input.match(/(?:radius|rounded)[:\s]+(\d+)\s*(?:px)?/i);
      if (radiusMatch) {
        changes.style.borderRadius = `${radiusMatch[1]}px`;
      } else if (lowerInput.includes('full')) {
        changes.style.borderRadius = '9999px';
      } else if (lowerInput.includes('none')) {
        changes.style.borderRadius = '0px';
      } else {
        changes.style.borderRadius = '8px'; // Default rounded
      }
    }

    // Padding
    if (lowerInput.includes('padding')) {
      const paddingMatch = input.match(/padding[:\s]+(\d+)\s*(?:px)?/i);
      if (paddingMatch) {
        changes.style.padding = `${paddingMatch[1]}px`;
      }
    }

    // Border
    // Skip for flex-containers - they use FlexContainerCommandHandler for specialized border handling
    const isFlexContainerType = component.type === ComponentType.FLEX_CONTAINER ||
                                component.type === ComponentType.CONTAINER;
    if (lowerInput.includes('border') && !isFlexContainerType) {
      if (lowerInput.includes('none') || lowerInput.includes('remove')) {
        changes.style.border = 'none';
      } else {
        const borderMatch = input.match(/border[:\s]+(\d+)\s*(?:px)?/i);
        if (borderMatch) {
          changes.style.border = `${borderMatch[1]}px solid #e5e7eb`;
        } else {
          changes.style.border = '1px solid #e5e7eb';
        }
      }
    }

    // Position modifications
    if (lowerInput.includes('position') || lowerInput.includes('move')) {
      const xMatch = input.match(/x[:\s]+(\d+)/i);
      const yMatch = input.match(/y[:\s]+(\d+)/i);

      if (xMatch || yMatch) {
        changes.position = {
          x: xMatch ? parseInt(xMatch[1], 10) : (component.position?.x ?? 0),
          y: yMatch ? parseInt(yMatch[1], 10) : (component.position?.y ?? 0),
          z: component.position?.z ?? 1,
        };
      }
    }

    // Visibility (but NOT for chart-specific elements like axes, legend, grid)
    const isChartElementVisibility = /\b(x[\s-]?axis|y[\s-]?axis|axes|legend|grid|tooltip|dots|points|markers|labels?|series)\b/i.test(lowerInput);
    if (!isChartElementVisibility) {
      if (lowerInput.includes('hide') || lowerInput.includes('hidden')) {
        if (!changes.properties) changes.properties = {};
        changes.properties.hidden = true;
      } else if (lowerInput.includes('show') || lowerInput.includes('visible')) {
        if (!changes.properties) changes.properties = {};
        changes.properties.hidden = false;
      }
    }

    // Lock/Unlock
    if (lowerInput.includes('lock')) {
      if (!changes.properties) changes.properties = {};
      changes.properties.locked = !lowerInput.includes('unlock');
    }

    const sanitizedChanges: PropertyChange = {};
    if (changes.properties && Object.keys(changes.properties).length > 0) {
      sanitizedChanges.properties = changes.properties;
    }
    if (changes.style && Object.keys(changes.style).length > 0) {
      sanitizedChanges.style = changes.style;
    }
    if (changes.size) sanitizedChanges.size = changes.size;
    if (changes.position) sanitizedChanges.position = changes.position;
    if (changes.data) sanitizedChanges.data = changes.data;
    if (changes.type) sanitizedChanges.type = changes.type;

    return Object.keys(sanitizedChanges).length > 0 ? sanitizedChanges : null;
  }

  /**
   * Normalize color values to hex or CSS color names
   */
  private normalizeColor(color: string): string {
    const colorMap: Record<string, string> = {
      'red': '#ef4444',
      'blue': '#3b82f6',
      'green': '#10b981',
      'yellow': '#eab308',
      'purple': '#a855f7',
      'pink': '#ec4899',
      'gray': '#6b7280',
      'grey': '#6b7280',
      'black': '#000000',
      'white': '#ffffff',
      'orange': '#f97316',
      'cyan': '#06b6d4',
      'indigo': '#6366f1',
    };

    const lowerColor = color.toLowerCase();
    if (colorMap[lowerColor]) {
      return colorMap[lowerColor];
    }

    // Return as-is if it's already a hex color
    if (color.startsWith('#')) {
      return color;
    }

    // Default to the original value
    return color;
  }

  /**
   * Validate property changes against component constraints
   */
  validateChanges(changes: PropertyChange, _component: Component): boolean {
    // Add validation logic based on component type and constraints
    if (changes.size) {
      if (changes.size.width !== undefined) {
        const width = typeof changes.size.width === 'number'
          ? changes.size.width
          : parseInt(String(changes.size.width), 10);
        if (isNaN(width) || width < 10 || width > 2000) {
          return false;
        }
      }
      if (changes.size.height !== undefined) {
        const height = typeof changes.size.height === 'number'
          ? changes.size.height
          : parseInt(String(changes.size.height), 10);
        if (isNaN(height) || height < 10 || height > 2000) {
          return false;
        }
      }
    }

    if (changes.properties?.fontSize) {
      const fontSize = getNumberValue(changes.properties.fontSize);
      if (fontSize === undefined) {
        return false;
      }
      if (fontSize < 8 || fontSize > 72) {
        return false;
      }
    }

    return true;
  }

  /**
   * Generate a human-readable description of property changes
   */
  describeChanges(changes: PropertyChange): string {
    // Phase 7: Handle preset info request - return formatted list
    if (changes.properties?._presetInfo) {
      return [
        '📦 Available Style Presets',
        '─────────────────────────',
        '',
        'card       → White card with shadow',
        '             padding: 24px | radius: 8px | shadow: md',
        '',
        'panel      → Bordered panel with light background',
        '             padding: 16px | radius: 4px | bg: #f9fafb',
        '',
        'outlined   → Transparent with border only',
        '             radius: 8px | border: 1px solid',
        '',
        'elevated   → Floating card with large shadow',
        '             radius: 12px | shadow: lg | bg: white',
        '',
        '─────────────────────────',
        'Usage: "make it a card" or just "card"'
      ].join('\n');
    }

    const descriptions: string[] = [];

    if (changes.size) {
      if (changes.size.width) descriptions.push(`width to ${changes.size.width}px`);
      if (changes.size.height) descriptions.push(`height to ${changes.size.height}px`);
    }

    if (changes.properties) {
      Object.entries(changes.properties).forEach(([key, value]) => {
        descriptions.push(`${key} to ${JSON.stringify(value)}`);
      });
    }

    if (changes.style) {
      Object.entries(changes.style).forEach(([key, value]) => {
        descriptions.push(`${key} to ${String(value)}`);
      });
    }

    if (changes.position) {
      descriptions.push(`position to (${changes.position.x}, ${changes.position.y})`);
    }

    if (changes.data) {
      descriptions.push(`updated chart data (${changes.data.length} points)`);
    }

    if (changes.type) {
      descriptions.push(`converted to ${changes.type}`);
    }

    return descriptions.length > 0
      ? `Changed ${descriptions.join(', ')}`
      : 'No changes made';
  }

  /**
   * Get AI command suggestions for a component
   * Now uses the new command system for better suggestions
   */
  async getSuggestions(component: Component): Promise<string[]> {
    // Use the new command parser for intelligent suggestions
    return commandParser.getSuggestions(component);
  }

  /**
   * Synchronous version for backward compatibility
   * @deprecated Use getSuggestions() instead
   */
  getSuggestionsSync(component: Component): string[] {
    // Basic suggestions for backward compatibility
    const suggestions: string[] = [];

    switch (component.type) {
      case ComponentType.TEXT:
        suggestions.push(
          "Make text bigger",
          "Center align",
          "Bold font",
          'Set text to "Hello World"',
          "Change color to blue"
        );
        break;
      case ComponentType.BUTTON:
        suggestions.push(
          "Make it primary",
          "Add rounded corners",
          'Set label to "Click Me"',
          "Change background to blue"
        );
        break;
      case ComponentType.INPUT:
        suggestions.push(
          'Set placeholder to "Enter text..."',
          "Make it a password field",
          "Add border",
          "Increase padding"
        );
        break;
      case ComponentType.IMAGE:
        suggestions.push(
          "Make it larger",
          "Add rounded corners",
          "Add border",
          "Center align"
        );
        break;
      case ComponentType.RADIAL_DIAL:
        suggestions.push(
          "Set range 0 to 255",
          "Theme neon-glow",
          "Set unit to %",
          "Toggle on/off",
          "Set fill color to orange"
        );
        break;
      case ComponentType.TOGGLE_SWITCH:
        suggestions.push(
          "Turn on",
          "Turn off",
          "Toggle",
          "Theme material",
          "Set on color to green",
          "Size large"
        );
        break;
      case ComponentType.STATUS_LIGHT:
        suggestions.push(
          "Set status to warning",
          "Blink fast",
          "Pulse",
          "Theme glow",
          "Shape diamond",
          "Size large"
        );
        break;
      case ComponentType.PUSH_BUTTON:
        suggestions.push(
          "Set shape to circular",
          "Theme neon",
          "Glow orange",
          "Press color red",
          "Size large"
        );
        break;
      case ComponentType.ICON_BUTTON:
        suggestions.push(
          "Set icon to Lightbulb",
          "Theme ghost",
          "Active color blue",
          "Shape circular",
          "Show label"
        );
        break;
      case ComponentType.STEPPER:
        suggestions.push(
          "Set range 0 to 100",
          "Step 5",
          "Value 50",
          "Unit degrees",
          "Theme rounded",
          "Size large"
        );
        break;
      case ComponentType.SLIDER:
        suggestions.push(
          "Set range 0 to 255",
          "Make vertical",
          "Show ticks",
          "Theme gradient",
          "Track color blue",
          "Unit percent"
        );
        break;
      case ComponentType.LEVEL_INDICATOR:
        suggestions.push(
          "Set segments to 10",
          "Warning at 30",
          "Critical at 10",
          "Make horizontal",
          "Theme battery",
          "Fill color green"
        );
        break;
      case ComponentType.SEGMENTED_CONTROL:
        suggestions.push(
          "Set options Auto Cool Heat Fan",
          "Select option 2",
          "Theme pill",
          "Equal width",
          "Make vertical",
          "Selected color blue"
        );
        break;
      case ComponentType.COLOR_PICKER:
        suggestions.push(
          "Set color to #FF6600",
          "Mode strip",
          "Show hex value",
          "Show RGB",
          "Theme compact",
          "Add preset colors"
        );
        break;
      case ComponentType.THERMOSTAT:
        suggestions.push(
          "Set target to 72",
          "Mode cool",
          "Use celsius",
          "Range 50 to 90",
          "Show humidity",
          "Theme minimal"
        );
        break;
      default:
        suggestions.push(
          "Make it bigger",
          "Change color",
          "Add padding",
          "Hide component"
        );
    }

    // Phase 2.3: Add style cloning suggestions
    suggestions.push(
      "Copy the style from header",
      "Match the button's colors"
    );

    return suggestions;
  }

  /**
   * Get example commands for a component type
   */
  async getExampleCommands(componentType: ComponentType): Promise<string[]> {
    return commandParser.getExampleCommands(componentType);
  }

  /**
   * Week 3: Multi-select support methods
   */

  /**
   * Parse commands for multiple components at once
   * Returns a map of component ID -> property changes
   *
   * Handles:
   * - Universal commands (hide, delete, move) that work on all components
   * - Type-specific commands that only apply to matching types
   * - Partial application when some components can't handle the command
   *
   * @param input Natural language command
   * @param components Array of selected components
   * @returns Map of component ID to property changes, or null if command can't be applied
   */
  async parseMultiSelectCommand(
    input: string,
    components: Component[]
  ): Promise<Map<string, PropertyChange> | null> {
    if (components.length === 0) return null;

    const changes = new Map<string, PropertyChange>();

    // Check if it's a universal command that applies to all components
    const universalCommand = this.tryUniversalCommand(input);
    if (universalCommand) {
      components.forEach(component => {
        changes.set(component.id, universalCommand);
      });
      return changes;
    }

    // Try to parse command for each component individually
    const parsedComponentChanges = await mapInBatches(
      components,
      MULTI_SELECT_PARSE_BATCH_SIZE,
      async (component) => ({
        component,
        changes: await this.parsePropertyChangesAsync(input, component),
      })
    );

    parsedComponentChanges.forEach(({ component, changes: componentChanges }) => {
      if (componentChanges) {
        changes.set(component.id, componentChanges);
      }
    });

    // Return changes if at least one component can handle the command
    if (changes.size > 0) {
      return changes;
    }

    console.warn('⚠️ [MULTI-SELECT] Command could not be applied to any component');
    return null;
  }

  /**
   * Check if a command is universal (applies to all component types)
   * Universal commands: hide/show, delete, move, opacity, lock/unlock
   */
  private tryUniversalCommand(input: string): PropertyChange | null {
    const trimmed = input.trim().toLowerCase();

    // Check if this is a chart axis command - don't treat as universal hide/show
    const isAxisCommand = /\b(x[\s-]?axis|y[\s-]?axis|axes)\b/i.test(trimmed);
    const isChartElementCommand = /\b(legend|grid|tooltip|dots|points|markers|labels?)\b/i.test(trimmed);
    const isSeriesCommand = /\bseries\b/i.test(trimmed);

    // Visibility commands (but NOT for chart-specific elements)
    if (!isAxisCommand && !isChartElementCommand && !isSeriesCommand) {
      if (trimmed === 'hide' || trimmed.startsWith('hide ')) {
        return { properties: { hidden: true } };
      }
      if (trimmed === 'show' || trimmed.startsWith('show ')) {
        return { properties: { hidden: false } };
      }
    }

    // Lock commands
    if (trimmed === 'lock' || trimmed.startsWith('lock ')) {
      return { properties: { locked: true } };
    }
    if (trimmed === 'unlock' || trimmed.startsWith('unlock ')) {
      return { properties: { locked: false } };
    }

    // Opacity commands
    const opacityMatch = trimmed.match(/(?:set |make )?opacity (?:to )?(\d+)%?/i);
    if (opacityMatch) {
      const opacity = parseInt(opacityMatch[1], 10) / 100;
      return { style: { opacity: Math.min(Math.max(opacity, 0), 1) } };
    }

    // Move commands (position changes)
    const moveMatch = trimmed.match(/move (?:by |to )?(?:x[:\s]+)?(-?\d+)(?:[,\s]+(?:y[:\s]+)?(-?\d+))?/i);
    if (moveMatch) {
      const deltaX = parseInt(moveMatch[1], 10);
      const deltaY = moveMatch[2] ? parseInt(moveMatch[2], 10) : 0;
      // Note: Actual position update would need current position, handled separately
      return { position: { x: deltaX, y: deltaY, z: 0 } };
    }

    return null;
  }

  /**
   * Check if a command is applicable to all selected components
   * Returns diagnostic info about why certain components can't handle the command
   */
  canApplyToSelection(input: string, components: Component[]): {
    canApply: boolean;
    applicableTo: Component[];
    notApplicableTo: Component[];
    reason?: string;
  } {
    if (components.length === 0) {
      return {
        canApply: false,
        applicableTo: [],
        notApplicableTo: [],
        reason: 'No components selected',
      };
    }

    // Universal commands can apply to all
    if (this.tryUniversalCommand(input)) {
      return {
        canApply: true,
        applicableTo: components,
        notApplicableTo: [],
      };
    }

    // Check each component individually
    const applicable: Component[] = [];
    const notApplicable: Component[] = [];

    components.forEach(component => {
      // Try quick pattern match
      const changes = this.parsePropertyChanges(input, component);
      if (changes && Object.keys(changes).length > 0) {
        applicable.push(component);
      } else {
        notApplicable.push(component);
      }
    });

    const canApply = applicable.length > 0;
    const reason = notApplicable.length > 0
      ? `Command only applies to ${applicable.length} of ${components.length} components (${[...new Set(notApplicable.map(c => c.type))].join(', ')} not supported)`
      : undefined;

    return {
      canApply,
      applicableTo: applicable,
      notApplicableTo: notApplicable,
      reason,
    };
  }

  /**
   * Get suggestions for multi-select context
   * Returns commands that work universally or across the selected component types
   */
  getMultiSelectSuggestions(components: Component[]): string[] {
    if (components.length === 0) return [];

    const suggestions: string[] = [];

    // Universal suggestions that always work
    suggestions.push(
      'Hide all',
      'Show all',
      'Lock all',
      'Unlock all',
      'Set opacity to 50%',
      'Move by x: 10'
    );

    // Type-specific suggestions if all components are the same type
    const uniqueTypes = [...new Set(components.map(c => c.type))];
    if (uniqueTypes.length === 1) {
      const singleType = uniqueTypes[0];
      switch (singleType) {
        case ComponentType.TEXT:
          suggestions.push(
            'Make all bigger',
            'Center align all',
            'Bold all',
            'Change color to blue'
          );
          break;
        case ComponentType.BUTTON:
          suggestions.push(
            'Make all primary',
            'Add rounded corners',
            'Change background to blue'
          );
          break;
        case ComponentType.FLEX_CONTAINER:
          suggestions.push(
            'Center all items',
            'Stack vertically',
            'Add gap between items'
          );
          break;
      }
    }

    // Mixed-type suggestions for text-like components
    const hasTextComponents = components.some(c =>
      c.type === ComponentType.TEXT || c.type === ComponentType.BUTTON
    );
    if (hasTextComponents && uniqueTypes.length > 1) {
      suggestions.push(
        'Change text color to blue (text/button only)',
        'Bold text (text/button only)'
      );
    }

    return suggestions;
  }
}

export const propertyModificationService = new PropertyModificationService();
